import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Search, User as UserIcon, LayoutDashboard, ArrowLeft, AlertCircle, Loader2, Plus, Settings } from 'lucide-react';
import { api } from '@onvice/portal-shell-api';
import NewPageDialog from '../../components/NewPageDialog';
import PageSettingsDialog from '../../components/PageSettingsDialog';

interface Person {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  user_role: string;
}

interface UserPage {
  id: string;
  name: string;
  icon: string | null;
  sort_order: number;
  updated_at: string;
  source_template_id?: string | null;
}

/** Admin-UI: persoenliche Seiten eines anderen Users verwalten.
 *  Joerg-Wunsch 2026-06-05.
 *
 *  Zwei Modi:
 *  - /admin/user-pages              → User-Picker (Personen-Liste mit Suche)
 *  - /admin/user-pages/:userId      → Pages des Users, Auswahl welche bearbeitet wird
 *  Klick auf eine Page navigiert zu /uebersicht/admin/:userId/page/:pageId
 *  (Layout-Editor identisch zur eigenen Seite, nur Save geht in den Pages des
 *  Ziel-Users — siehe UebersichtFuehrung actAsUserId-Modus).
 *
 *  MVP-Scope: nur Layout-Bearbeitung. Pages anlegen / umbenennen / loeschen im
 *  Auftrag eines Users folgt nach. */
export default function AdminUserPagesPage() {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId?: string }>();

  // Personen-Liste (User-Picker-Modus + Header im Pages-Modus)
  const [persons, setPersons] = useState<Person[]>([]);
  const [personsLoading, setPersonsLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setPersonsLoading(true);
    api.get<{ success: boolean; data: Person[] }>('/admin/adressbuch/persons')
      .then((r) => { if (r.success) setPersons(r.data); })
      .catch(() => setPersons([]))
      .finally(() => setPersonsLoading(false));
  }, []);

  const filteredPersons = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = persons.filter((p) => p.user_id); // nur Personen mit User-Konto
    if (!q) return list;
    return list.filter((p) =>
      p.first_name.toLowerCase().includes(q)
      || p.last_name.toLowerCase().includes(q)
      || p.email.toLowerCase().includes(q),
    );
  }, [persons, search]);

  // Pages des gewaehlten Users
  const [pages, setPages] = useState<UserPage[] | null>(null);
  const [pagesError, setPagesError] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [settingsTarget, setSettingsTarget] = useState<UserPage | null>(null);

  const loadPages = useCallback(() => {
    if (!userId) { setPages(null); setPagesError(null); return; }
    api.get<{ success: boolean; data: UserPage[] }>(`/portal/admin/users/${userId}/pages`)
      .then((r) => {
        if (!r.success) { setPagesError('Pages konnten nicht geladen werden'); return; }
        setPages(r.data);
        setPagesError(null);
      })
      .catch(() => setPagesError('Fehler beim Laden'));
  }, [userId]);

  useEffect(() => { loadPages(); }, [loadPages]);

  const selectedPerson = useMemo(
    () => persons.find((p) => p.user_id === userId) ?? null,
    [persons, userId],
  );

  // ============== User-Picker-Modus ==============
  if (!userId) {
    return (
      <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-4">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <LayoutDashboard size={22} /> Seiten eines Users verwalten
          </h1>
          <p className="text-sm text-muted-foreground">
            User auswählen, dessen persönliche Übersichts-Seiten du anpassen möchtest. Anschließend kannst du das Layout jeder Seite genauso bearbeiten wie der User es selbst täte.
          </p>
        </header>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              placeholder="Name oder E-Mail suchen…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-card border border-default text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
            />
          </div>
        </div>

        {personsLoading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : filteredPersons.length === 0 ? (
          <div className="text-center text-muted-foreground py-10 text-sm">
            Keine Personen gefunden.
          </div>
        ) : (
          <ul className="divide-y divide-default rounded-lg border border-default overflow-hidden bg-card">
            {filteredPersons.map((p) => (
              <li key={p.user_id}>
                <Link
                  to={`/admin/user-pages/${p.user_id}`}
                  className="flex items-center gap-3 p-3 hover:bg-muted transition-colors"
                >
                  <UserIcon size={16} className="text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{p.first_name} {p.last_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{p.email}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-slate-800 text-slate-400 shrink-0">
                    {p.user_role}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  // ============== Pages-Liste-Modus ==============
  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-4">
      <header className="space-y-1">
        <button
          type="button"
          onClick={() => navigate('/admin/user-pages')}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={14} /> Anderen User wählen
        </button>
        <div className="flex items-start justify-between gap-3 flex-wrap mt-1">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <LayoutDashboard size={22} />
            Seiten von{' '}
            {selectedPerson
              ? <span>{selectedPerson.first_name} {selectedPerson.last_name}</span>
              : <span className="text-muted-foreground">User {userId.slice(0, 8)}…</span>}
          </h1>
          <button
            type="button"
            onClick={() => setNewOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-600 text-primary-foreground hover:bg-brand-500 text-sm"
            title="Neue Seite im Auftrag des Users anlegen"
          >
            <Plus size={14} /> Neue Seite
          </button>
        </div>
        <p className="text-sm text-muted-foreground">
          Klick auf eine Seite öffnet den Layout-Editor. Save speichert in die Pages des Users. Über das Zahnrad-Icon kannst du eine Seite umbenennen, zurücksetzen oder löschen.
        </p>
      </header>

      {pagesError && (
        <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-sm flex items-center gap-2">
          <AlertCircle size={16} /> {pagesError}
        </div>
      )}

      {!pages ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : pages.length === 0 ? (
        <div className="text-center text-muted-foreground py-10 text-sm border border-dashed border-default rounded-lg">
          Dieser User hat noch keine persönlichen Seiten. Lege oben rechts eine neue Seite an.
        </div>
      ) : (
        <ul className="divide-y divide-default rounded-lg border border-default overflow-hidden bg-card">
          {pages.map((p) => (
            <li key={p.id} className="flex items-center gap-1 hover:bg-muted transition-colors">
              <Link
                to={`/uebersicht/admin/${userId}/page/${p.id}`}
                className="flex items-center gap-3 p-3 flex-1 min-w-0"
              >
                <LayoutDashboard size={16} className="text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground">
                    zuletzt geändert {new Date(p.updated_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </div>
                </div>
              </Link>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSettingsTarget(p); }}
                className="p-2 mr-2 text-muted-foreground hover:text-foreground rounded hover:bg-background min-h-[40px] min-w-[40px] inline-flex items-center justify-center"
                title="Umbenennen / Zurücksetzen / Löschen"
                aria-label="Seite verwalten"
              >
                <Settings size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <NewPageDialog
        open={newOpen}
        actAsUserId={userId}
        onClose={() => setNewOpen(false)}
        onCreated={(newPageId) => {
          setNewOpen(false);
          navigate(`/uebersicht/admin/${userId}/page/${newPageId}`);
        }}
      />

      {settingsTarget && (
        <PageSettingsDialog
          open={true}
          pageId={settingsTarget.id}
          initialName={settingsTarget.name}
          hasTemplate={!!settingsTarget.source_template_id}
          actAsUserId={userId}
          onClose={() => setSettingsTarget(null)}
          onRenamed={() => { setSettingsTarget(null); loadPages(); }}
          onDeleted={() => { setSettingsTarget(null); loadPages(); }}
          onReset={() => { setSettingsTarget(null); loadPages(); }}
        />
      )}
    </div>
  );
}
